import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {updateMyProfile, changePassword} from "../api/api";
import {useAlert} from "../context/AlertContext";
import {useAuth} from "../context/AuthContext";

const ProfilePage = () => {
    const {t} = useTranslation();

    const {user, isLoading, refreshProfile} = useAuth();
    const {showAlert} = useAlert();

    const [fullName, setFullName] = useState("");
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [repeatNewPassword, setRepeatNewPassword] = useState("");

    const isPasswordMismatch =
        !!newPassword && !!repeatNewPassword && newPassword !== repeatNewPassword;

    useEffect(() => {
        if (user?.fullName) setFullName(user.fullName);
    }, [user]);

    const handleUpdateProfile = async () => {
        try {
            await updateMyProfile(fullName);
            showAlert(t("profile.alerts.profileUpdated"), "success");
            await refreshProfile();
        } catch {
            showAlert(t("profile.alerts.profileUpdateFailed"), "error");
        }
    };

    const handleChangePassword = async () => {
        try {
            await changePassword(oldPassword, newPassword);
            showAlert(t("profile.alerts.passwordChanged"), "success");
            setOldPassword("");
            setNewPassword("");
            setRepeatNewPassword("");
        } catch (error: any) {
            showAlert(
                t("profile.alerts.unexpectedError", {
                    message: error?.message ?? t("profile.unknownError"),
                }),
                "error"
            );
        }
    };

    if (isLoading) {
        return (
            <Container maxWidth="sm" sx={{mt: 10, textAlign: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (!user) {
        return (
            <Container maxWidth="sm" sx={{mt: 10, textAlign: "center"}}>
                <Typography variant="h6" color="error">
                    {t("profile.failedToFetch")}
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Paper
                elevation={0}
                sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    p: 3,
                })}
            >
                <Box sx={{mb: 2}}>
                    <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                        {t("profile.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("profile.subtitle")}
                    </Typography>
                </Box>

                <Divider sx={{mb: 2}}/>

                <Box>
                    <Typography sx={{fontWeight: 700, mb: 1}}>
                        {t("profile.personalInfo")}
                    </Typography>

                    <TextField
                        fullWidth
                        label={t("profile.email")}
                        value={user.email}
                        margin="normal"
                        disabled
                    />

                    <TextField
                        fullWidth
                        label={t("profile.fullName")}
                        value={fullName}
                        margin="normal"
                        onChange={(e) => setFullName(e.target.value)}
                    />

                    <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                        <Button
                            variant="outlined"
                            onClick={handleUpdateProfile}
                            disabled={!fullName.trim()}
                        >
                            {t("profile.saveChanges")}
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{my: 3}}/>

                <Box>
                    <Typography sx={{fontWeight: 700, mb: 1}}>
                        {t("profile.changePassword")}
                    </Typography>

                    <TextField
                        fullWidth
                        label={t("profile.currentPassword")}
                        type="password"
                        margin="normal"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label={t("profile.newPassword")}
                        type="password"
                        margin="normal"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label={t("profile.repeatNewPassword")}
                        type="password"
                        margin="normal"
                        value={repeatNewPassword}
                        onChange={(e) => setRepeatNewPassword(e.target.value)}
                        error={isPasswordMismatch}
                        helperText={isPasswordMismatch ? t("profile.passwordsDoNotMatch") : ""}
                    />

                    <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2, gap: 1}}>
                        <Button
                            variant="contained"
                            onClick={handleChangePassword}
                            disabled={
                                isPasswordMismatch ||
                                !oldPassword.trim() ||
                                !newPassword.trim() ||
                                !repeatNewPassword.trim()
                            }
                        >
                            {t("profile.changePassword")}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Container>
    );
};

export default ProfilePage;